import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { authenticatedRequest } from "@/lib/graphql/client";
import {
  ADMIN_FILE_HISTORIES,
  ADMIN_FILE_HISTORY_CONTENT,
} from "@/lib/graphql/queries/file-history";
import { ADMIN_RESTORE_FILE_HISTORY } from "@/lib/graphql/mutations/file-history";
import type { FileHistoryContent, FileHistoryItem } from "@/types/file-history";

export const fileHistoryKeys = {
  all: ["fileHistory"] as const,
  list: (fileId: string) => [...fileHistoryKeys.all, "list", fileId] as const,
  content: (historyId: string) =>
    [...fileHistoryKeys.all, "content", historyId] as const,
};

export function fileHistoriesQueryOptions(fileId: string | null) {
  return queryOptions({
    queryKey: fileHistoryKeys.list(fileId ?? ""),
    queryFn: async () => {
      const data = await authenticatedRequest<{
        adminFileHistories: FileHistoryItem[];
      }>(ADMIN_FILE_HISTORIES, { fileId });
      return data.adminFileHistories;
    },
    enabled: Boolean(fileId),
    staleTime: 30000,
  });
}

export function fileHistoryContentQueryOptions(historyId: string | null) {
  return queryOptions({
    queryKey: fileHistoryKeys.content(historyId ?? ""),
    queryFn: async () => {
      const data = await authenticatedRequest<{
        adminFileHistoryContent: FileHistoryContent | null;
      }>(ADMIN_FILE_HISTORY_CONTENT, { historyId });
      return data.adminFileHistoryContent;
    },
    enabled: Boolean(historyId),
    staleTime: 60000,
  });
}

export function useRestoreFileHistory(fileId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (historyId: string) => {
      const data = await authenticatedRequest<{
        adminRestoreFileHistory: {
          _id: string;
          title: string;
          updatedAt: string;
        };
      }>(ADMIN_RESTORE_FILE_HISTORY, { historyId });
      return data.adminRestoreFileHistory;
    },
    onSuccess: () => {
      if (fileId) {
        queryClient.invalidateQueries({
          queryKey: fileHistoryKeys.list(fileId),
        });
      }
      toast.success("복원이 완료되었습니다");
    },
    onError: (error: Error) => {
      toast.error("복원 실패", {
        description: error.message || "다시 시도해주세요.",
      });
    },
  });
}
